import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import {
    BriefcaseBusiness,
    Building2,
    PlusIcon,
} from 'lucide-react';
import { Pencil1Icon } from '@radix-ui/react-icons';
import axios from '../../lib/axios';
import { useIsUserAdmin } from '../../hooks/authHelpers';
import { useToast } from "../../hooks/use-toast.js";

import {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
} from '../../components/ui/card.jsx';
import { Button } from '../../components/ui/button.jsx';
import { Skeleton } from '../../components/ui/skeleton.jsx';
import { Separator } from '../../components/ui/separator.jsx';
import { Badge } from '../../components/ui/badge.jsx';
import { Input } from '../../components/ui/input.jsx';

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table.jsx';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select.jsx";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination"

// ─────────────────────────────────────────────────────────────────────────────
// LOADING SKELETONS
// ─────────────────────────────────────────────────────────────────────────────

const CompanyPageSkeleton = () => (
    <div className="min-h-full min-w-full p-4 md:p-6">
        <Card className="w-full max-w-7xl mx-auto shadow-lg">
            <CardHeader className="flex flex-row justify-between items-center">
                <Skeleton className="h-8 w-[300px]" />
                <div className="flex gap-2">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <Skeleton className="h-10 w-10 rounded-full" />
                </div>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[...Array(6)].map((_, index) => (
                        <div key={index} className="flex items-center space-x-3 bg-gray-50 p-3 rounded-lg">
                            <Skeleton className="h-6 w-6 rounded-full" />
                            <div className="space-y-2 flex-1">
                                <Skeleton className="h-4 w-[100px]" />
                                <Skeleton className="h-4 w-[200px]" />
                            </div>
                        </div>
                    ))}
                </div>

                <Separator className="my-8" />

                <div className="w-full">
                    <div className="grid w-full grid-cols-2 md:grid-cols-4 gap-2 p-1 rounded-lg mb-8">
                        {[...Array(4)].map((_, index) => (
                            <Skeleton key={index} className="h-10 rounded-md" />
                        ))}
                    </div>

                    <div className="space-y-6">
                        {[...Array(3)].map((_, index) => (
                            <Skeleton key={index} className="h-32 w-full rounded-lg" />
                        ))}
                    </div>
                </div>
            </CardContent>
        </Card>
    </div>
);

// Skeleton row component for communities table loading state
const TableRowSkeleton = () => (
    <TableRow>
        <TableCell>
            <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
        </TableCell>
        <TableCell>
            <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse" />
        </TableCell>
        <TableCell>
            <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse" />
        </TableCell>
        <TableCell>
            <div className="h-4 w-1/3 bg-gray-200 rounded animate-pulse" />
        </TableCell>
        <TableCell>
            <div className="h-4 w-1/4 bg-gray-200 rounded animate-pulse" />
        </TableCell>
    </TableRow>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const CompanyPage = () => {
    const { id: companyId } = useParams();
    const navigate = useNavigate();
    const [company, setCompany] = useState(null);
    const [isLoadingCompany, setIsLoadingCompany] = useState(true);
    const { toast } = useToast();
    const isAdmin = useIsUserAdmin();

    // ─────────────────────────────────────────────────────────────────────────
    // Fetch Company Info
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const response = await axios.get(`/companies/${companyId}`);
                setCompany(response.data);
            } catch (error) {
                console.error('Error fetching company data:', error);
                toast({
                    title: "Error",
                    description: "Failed to load company details. Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsLoadingCompany(false);
            }
        };

        fetchCompany();
    }, [companyId, toast]);

    // ─────────────────────────────────────────────────────────────────────────
    // Communities Table States & Logic
    // ─────────────────────────────────────────────────────────────────────────

    const [searchParams, setSearchParams] = useSearchParams();
    const [communities, setCommunities] = useState([]);
    const [isLoadingCommunities, setIsLoadingCommunities] = useState(true);

    // Pagination state
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 10,
        total_pages: 0,
    });

    // Sync page & limit with URL query params or fallback
    const pageParam = searchParams.get('page');
    const limitParam = searchParams.get('limit');

    // Search filter (debounced)
    const searchTerm = searchParams.get('search') || '';
    // Status filter
    const statusValue = searchParams.get('status') || (isAdmin ? 'active' : 'all');

    // Update local pagination state from searchParams
    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            page: pageParam ? parseInt(pageParam, 10) : 1,
            limit: limitParam ? parseInt(limitParam, 10) : 10,
        }));
    }, [pageParam, limitParam]);

    // Debounce updating searchParams
    const debouncedSetSearchParams = useCallback(
        debounce((params) => {
            setSearchParams(params);
        }, 300),
        [setSearchParams]
    );

    // Handle search change
    const handleSearchChange = ({ target }) => {
        const { value } = target;
        if (value) {
            searchParams.set('search', value);
        } else {
            searchParams.delete('search');
        }
        // Reset page to 1 whenever search changes
        searchParams.delete('page');
        debouncedSetSearchParams(searchParams);
    };

    // Handle status filter
    const handleStatusFilter = (status) => {
        if ((isAdmin && status !== 'active') || (!isAdmin && status !== 'all')) {
            searchParams.set('status', status);
        } else {
            searchParams.delete('status');
        }
        // Reset page to 1 whenever status changes
        searchParams.delete('page');
        setSearchParams(searchParams);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        searchParams.set('page', newPage);
        setSearchParams(searchParams);
    };

    // Handle creating community
    const handleCreateCommunity = () => {
        navigate('/communities/create');
    };

    // Outgoing Call Answer Rate helper
    const communityAnswerRate = (community) => {
        if (parseInt(community.total_outgoing_calls_count) === 0) {
            return 0;
        }
        return (
            (parseInt(community.total_outgoing_answered_calls_count) /
                parseInt(community.total_outgoing_calls_count)) *
            100
        );
    };

    // Fetch communities for this company
    useEffect(() => {
        const fetchCommunities = async () => {
            try {
                setIsLoadingCommunities(true);

                const params = {
                    page: pagination.page,
                    limit: pagination.limit,
                };

                // Only filter by status if not 'all' (non-admin) or 'active' (admin default)
                if (statusValue !== 'all') {
                    params.status = statusValue;
                }
                if (searchTerm) {
                    params.search = searchTerm;
                }

                // Important: pass company_ids[] param
                // e.g., company_ids[]=123
                params['company_ids[]'] = companyId;

                const queryString = new URLSearchParams(params).toString();
                const response = await axios.get(`/communities?${queryString}`);

                setCommunities(response.data.data);
                setPagination({
                    total: response.data.pagination.total,
                    page: response.data.pagination.page,
                    limit: response.data.pagination.limit,
                    total_pages: response.data.pagination.total_pages,
                });
            } catch (error) {
                console.error('Error fetching communities:', error);
                toast({
                    title: "Error",
                    description: "Failed to load communities. Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsLoadingCommunities(false);
            }
        };

        fetchCommunities();
    }, [companyId, pagination.page, pagination.limit, statusValue, searchTerm, toast]);

    // ─────────────────────────────────────────────────────────────────────────
    // RENDERING
    // ─────────────────────────────────────────────────────────────────────────

    if (isLoadingCompany) {
        // While the company info is loading, show the full page skeleton
        return <CompanyPageSkeleton />;
    }

    if (!company) {
        return (
            <div className="flex items-center justify-center min-h-[400px] text-gray-500">
                <div className="text-center space-y-2">
                    <Building2 className="h-12 w-12 mx-auto text-gray-400" />
                    <p className="text-lg font-medium">Company not found</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-full min-w-full p-4 md:p-6">
            <Card className="w-full max-w-7xl mx-auto shadow-lg">
                {/* ────────────── Company Info ────────────── */}
                <CardHeader className="flex flex-row justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <BriefcaseBusiness className="h-8 w-8 text-primary" />
                        <CardTitle className="text-3xl font-bold justify-center text-center">
                            <div className="inline-block">
                                {company.name}
                                <Badge className="bg-blue-500 ml-2 inline-block align-middle">
                                    <span className="font-semibold">ID: </span>
                                    {company.id}
                                </Badge>
                            </div>
                        </CardTitle>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" className="flex items-center gap-2" disabled onClick={() => navigate(`/companies/${companyId}/edit`)}>
                            <Pencil1Icon className="h-4 w-4" />
                            Edit
                        </Button>
                    </div>
                </CardHeader>

                <CardContent>
                    {/* Add any additional company info fields here, or remove if not needed. */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {/* Example info card or fields */}
                        {/* <InfoCard icon={Calendar} label="Created At" value={company.created_at} /> */}
                    </div>

                    <Separator className="my-8" />

                    {/* ────────────── Communities List ────────────── */}
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-2 flex-grow">
                                {/* <MagnifyingGlassIcon className="w-4 h-4 text-gray-500" /> */}
                                <Input
                                    type="text"
                                    placeholder="Search communities..."
                                    defaultValue={searchTerm}
                                    onChange={handleSearchChange}
                                    className="flex-grow"
                                />
                            </div>

                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className={isAdmin ? "" : "inline-block"}>
                                            <Button
                                                disabled
                                                onClick={handleCreateCommunity}
                                                className="ml-4"
                                            >
                                                <PlusIcon className="w-4 h-4 mr-2" />
                                                Create Community
                                            </Button>
                                        </div>
                                    </TooltipTrigger>
                                    {!isAdmin && (
                                        <TooltipContent>
                                            <p>Please contact support for creating a community.</p>
                                        </TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                        </div>

                        <div className='flex justify-end mt-5 mb-4'>
                            <div className="flex items-center space-x-2">
                                <Select value={statusValue} onValueChange={handleStatusFilter}>
                                    <SelectTrigger className="w-[280px]">
                                        <SelectValue placeholder="Filter status..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All</SelectItem>
                                        <SelectItem value="active">Active</SelectItem>
                                        <SelectItem value="deactived">Deactivated</SelectItem>
                                        <SelectItem value="onboarding">Onboarding</SelectItem>
                                        <SelectItem value="secret-shopper">Secret Shopper</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[35%]">Community Name</TableHead>
                                        <TableHead className="w-[15%]">Community Phone</TableHead>
                                        <TableHead className="w-[15%]">Flair Phone</TableHead>
                                        <TableHead className="w-[15%]">Status</TableHead>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <TableHead className="w-[20%]">
                                                        LTAR % (30 days)
                                                    </TableHead>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p className="text-sm">
                                                        Leasing Team Answer Rate: The percentage of outgoing calls that were answered by the community
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {isLoadingCommunities
                                        ? // Loading skeleton rows
                                        [...Array(pagination.limit)].map((_, index) => (
                                            <TableRowSkeleton key={index} />
                                        ))
                                        : communities.length > 0
                                            ? communities.map((community) => (
                                                <TableRow key={community.id}>
                                                    <TableCell className="font-medium">
                                                        <Link
                                                            to={`/communities/${community.id}`}
                                                            className="underline"
                                                        >
                                                            {community.community_name}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>
                                                        {community.community_phone_number}
                                                    </TableCell>
                                                    <TableCell>
                                                        {community.assigned_flair_phone_number}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="secondary" className="font-normal">
                                                            {community.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        {parseFloat(communityAnswerRate(community)).toFixed(2)}%
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                            : // No communities found
                                            (
                                                <TableRow>
                                                    <TableCell colSpan={5} className="text-center py-8">
                                                        <Badge variant="secondary">
                                                            No communities found
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                    }
                                </TableBody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {!isLoadingCommunities && pagination.total_pages > 1 && (
                            <div className="flex justify-center mt-6">
                                <Pagination
                                    currentPage={pagination.page}
                                    lastPage={pagination.total_pages}
                                >
                                    <PaginationContent>
                                        <PaginationItem>
                                            <PaginationPrevious
                                                onClick={() =>
                                                    handlePageChange(
                                                        Math.max(pagination.page - 1, 1)
                                                    )
                                                }
                                            />
                                        </PaginationItem>

                                        {[...Array(pagination.total_pages)].map((_, index) => {
                                            const pageNumber = index + 1;
                                            // Show first page, last page, and up to 1 page around current page
                                            if (
                                                pageNumber === 1 ||
                                                pageNumber === pagination.total_pages ||
                                                (pageNumber >= pagination.page - 1 &&
                                                    pageNumber <= pagination.page + 1)
                                            ) {
                                                return (
                                                    <PaginationItem key={pageNumber}>
                                                        <PaginationLink
                                                            onClick={() =>
                                                                handlePageChange(pageNumber)
                                                            }
                                                            isActive={pageNumber === pagination.page}
                                                        >
                                                            {pageNumber}
                                                        </PaginationLink>
                                                    </PaginationItem>
                                                );
                                            }
                                            // Show ellipsis for large gap
                                            if (
                                                pageNumber === 2 ||
                                                pageNumber === pagination.total_pages - 1
                                            ) {
                                                return (
                                                    <PaginationItem key={pageNumber}>
                                                        <PaginationEllipsis />
                                                    </PaginationItem>
                                                );
                                            }
                                            return null;
                                        })}

                                        <PaginationItem>
                                            <PaginationNext
                                                onClick={() =>
                                                    handlePageChange(
                                                        Math.min(
                                                            pagination.page + 1,
                                                            pagination.total_pages
                                                        )
                                                    )
                                                }
                                            />
                                        </PaginationItem>
                                    </PaginationContent>
                                </Pagination>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default CompanyPage;
